/**
 * Agent registry card — one agent in the card/grid view of Agent Registry.
 *
 * Kept separate from the CordioAbp table so the gallery layout can evolve without
 * touching column/action contracts the E2E grid checklist selects on.
 */

import {
  Button,
  Chip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@coordio/ui';
import { Copy, Eye, SquarePen, Trash2 } from 'lucide-react';

import type { AgentDefinitionDto } from '../../types/agent';

export interface AgentCardProps {
  agent: AgentDefinitionDto;
  onView: (agent: AgentDefinitionDto) => void;
  onEdit: (agent: AgentDefinitionDto) => void;
  onClone: (agent: AgentDefinitionDto) => void;
  onDelete: (agent: AgentDefinitionDto) => void;
}

export const AgentCard = ({ agent, onView, onEdit, onClone, onDelete }: AgentCardProps) => {
  return (
    <article
      className="agent-card"
      data-testid="agent-card"
      onClick={() => onView(agent)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView(agent);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`View agent ${agent.name}`}
    >
      <div className="agent-card__body">
        <header className="agent-card__header">
          <h3 className="agent-card__title">
            {agent.iconName ? (
              <span className="agent-card__icon" aria-hidden>
                {agent.iconName}
              </span>
            ) : null}
            <span className="agent-card__name">{agent.name}</span>
          </h3>
          {agent.slug ? <span className="agent-card__slug">{agent.slug}</span> : null}
        </header>

        <p className="agent-card__description">
          {agent.description?.trim() || 'No description available'}
        </p>

        <div className="agent-card__chips">
          {agent.category ? <Chip label={agent.category} color="violet" variant="outlined" /> : null}
          <Chip label={agent.isSystem ? 'System' : 'Custom'} color={agent.isSystem ? 'red' : 'default'} />
          <Chip label={agent.isActive ? 'Active' : 'Inactive'} color={agent.isActive ? 'green' : 'default'} />
        </div>

        {agent.version ? (
          <p className="agent-card__version">Version: {agent.version}</p>
        ) : null}
      </div>

      <footer className="agent-card__actions" onClick={(e) => e.stopPropagation()}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="View agent"
                onClick={() => onView(agent)}
              >
                <Eye className="h-4 w-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent>View Details</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {!agent.isSystem ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Edit agent"
                  onClick={() => onEdit(agent)}
                >
                  <SquarePen className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Clone agent"
                onClick={() => void onClone(agent)}
              >
                <Copy className="h-4 w-4" aria-hidden />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Clone</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {!agent.isSystem ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="agent-card__action--destructive"
                  aria-label="Delete agent"
                  onClick={() => onDelete(agent)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </footer>
    </article>
  );
};
